import {
  GymTypes, ITimeSlotInfo, TimeSlotType, TimeSpan, TimeZoneMapping,
} from 'fitbeat.models';
import { BookingsConfig } from '../../config/bookings';
import { GymInfoForTimeslots, UserProfile } from '../../entities';
import { Day, TimeType } from '../../helpers';
import { IGymTimeSlotsRepository } from '../../persistence';

export class GymTimeSlotsManager {
  public static initializeInBackground(gymTimeSlotsRepo: IGymTimeSlotsRepository) {
    // TODO: create async static initialize function
    gymTimeSlotsRepo.createDefaultDoc()
      .catch((e) => { /* no logger, no-op */ });
    return new GymTimeSlotsManager(gymTimeSlotsRepo);
  }

  private constructor(private gymTimeSlotsRepo: IGymTimeSlotsRepository) {
  }

  public async updateCache() {
    await this.gymTimeSlotsRepo.updateCache();
  }

  public async getTimeSlotInfo(
    date: Date,
    userProfile: UserProfile | undefined,
    gym: GymInfoForTimeslots,
  ): Promise<ITimeSlotInfo> {
    const slotInfo = await this.getTimeInfoWithoutBetaUserOverride(date, gym);

    const useOffPeakForAllSlotsExceptPeakForUser = userProfile?.has24HourAccess;
    if (slotInfo.type !== TimeSlotType.Peak && useOffPeakForAllSlotsExceptPeakForUser) {
      return {
        type: TimeSlotType.OffPeak,
        reason: slotInfo.reason,
      };
    }

    return slotInfo;
  }

  public async getAllNormalDayGymTimeSlots(gym: GymInfoForTimeslots) {
    const days = gym.overridenTimeSlotTypes || await this.gymTimeSlotsRepo.getAll();
    return days;
  }

  public async getSpecialDayTimeSlot(
    gym: GymInfoForTimeslots,
    fromYYYYMMDD: string,
  ): Promise<Day | 'Not a Special Day' | 'Closed All Day'> {
    const from = TimeZoneMapping.dateInTimezoneToUTCDate(fromYYYYMMDD, gym.timezone);
    const timeTypes: TimeType[] = [];
    let onlyHasNormalDayTimeSlots = true;

    const slotStepIncrement = 0.5; // step by half an hour to allow on-half-hour scheduling
    for (let startHour = 0; startHour < 24; startHour += slotStepIncrement) {
      const slotFrom = TimeSpan.getStartOfDayInTimezonePlusHours(from, gym.timezone, startHour);
      const slotTo = TimeSpan.getStartOfDayInTimezonePlusHours(from, gym.timezone, startHour + slotStepIncrement);

      const slotInfo = await this.getTimeInfoWithoutBetaUserOverride(slotFrom, gym);
      onlyHasNormalDayTimeSlots = onlyHasNormalDayTimeSlots && slotInfo.reason === 'normal';

      if (!slotInfo.type) {
        // implicitly closed
      } else if (slotInfo.type === 'Cancelled') {
        // ignore cancelled slots
      } else {
        const formattedStart = TimeZoneMapping.dateToFormattedLocalTime(slotFrom, gym.timezone, 'HHmm');
        const formattedEnd = TimeZoneMapping.dateToFormattedLocalTime(slotTo, gym.timezone, 'HHmm');
        timeTypes.push(new TimeType(new TimeSpan(formattedStart, formattedEnd), slotInfo.type));
      }
    }

    if (onlyHasNormalDayTimeSlots) {
      return 'Not a Special Day';
    }
    if (timeTypes.length === 0) {
      return 'Closed All Day';
    }

    return new Day(timeTypes);
  }

  private async getTimeInfoWithoutBetaUserOverride(
    date: Date,
    gym: GymInfoForTimeslots,
  ): Promise<ITimeSlotInfo> {
    const timeString = TimeZoneMapping.dateToFormattedLocalTime(date, gym.timezone, 'HHmm');
    const dayOfWeek = TimeZoneMapping.getDayOfWeekNumber(date, gym.timezone);
    // TODO: check other date vs dayOfWeek usages here

    if (gym.type === GymTypes.Home) {
      const anytimeDay = BookingsConfig.generateAnytimeDays()[date.getDay()];
      const homeSlotType = anytimeDay.getTimeSlotType(timeString);
      return {
        type: homeSlotType,
        reason: 'normal',
      };
    }
    if (this.isTimeSlotCancelled(gym, date, timeString)) {
      return {
        type: 'Cancelled',
        reason: 'cancelled',
      };
    }
    const specificSlotType = gym.specificOperatingTimes.get(date);
    if (specificSlotType === TimeSlotType.None) {
      /*
       * TODO: #2501: none and undefined have different symantics (at least in tests?)
       * but None is used to define an explicitly closed slot from SpecificOperatingTimes
       * cleanup in #2501
       */
      return {
        type: undefined,
        reason: 'specific-hours',
      };
    }
    if (specificSlotType) {
      return {
        type: specificSlotType,
        reason: 'specific-hours',
      };
    }

    const days = await this.getAllNormalDayGymTimeSlots(gym);
    const day = days[dayOfWeek];
    const generalTypeOnDayOfWeek = day.getTimeSlotType(timeString);
    return {
      type: generalTypeOnDayOfWeek,
      reason: 'normal',
    };
  }

  private isTimeSlotCancelled(gym: GymInfoForTimeslots, date: Date, timeString: string): boolean {
    const slotsCancelledOnDay = gym.cancelledTimes.get(date);
    // TODO: this currently assumes the whole day is cancelled. can use more granular times if needed
    return !!slotsCancelledOnDay;
  }
}
